/** @jsxImportSource ./simboluri-jsx */
// Harta gropilor de pământ: lotul, toate gropile, și cea folosită cu stea.
//
// De ce o hartă și nu o listă. Costul evacuării e o balanță între distanță și
// tarif, iar distanța e singura jumătate pe care omul o poate JUDECA — el știe
// că drumul spre Glina trece prin centură la ore imposibile, lucru pe care
// niciun tabel nu-l conține. Lista dă cifrele, harta dă contextul în care ele
// se pot contrazice; de asta sunt amândouă pe ecran, nu una.
//
// Lecțiile din `pages/Harta.tsx` se aplică identic și sunt repetate aici
// fiindcă sunt scumpe: harta se creează în `useEffect` (pe un `div` de
// dimensiune zero `L.map()` iese greșit), dalele vin din configurația
// serverului, iar încadrarea se reface la redimensionare — părintele e un flex
// care se așază DUPĂ prima randare, iar un `fitBounds` de atunci dă zoom 0.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useT } from "../i18n";
import type { Cheie } from "../i18n";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import { configHarta } from "../pages/dosarclient";
import type { ConfigHarta } from "../pages/dosarclient";
import type { GroapaOptiune, GropiRaspuns } from "./apiStructura";
import { bani } from "./bani";

// Lotul: un punct plin, vizibil peste dale. Nu e un pin — un pin arată ca o
// alegere, iar lotul nu se alege.
const LOT = L.divIcon({
  className: "",
  html: '<div class="gh-lot" title="lotul"></div>',
  iconSize: [18, 18], iconAnchor: [9, 9],
});

// CÂT DE MARE E ZONA, când nu știm punctul. Un studiu geotehnic care spune doar
// „Voluntari, Ilfov" ne dă centrul comunei, nu parcela — desenat ca un punct, ar
// arăta exact ca o coordonată din cadastru. Discul spune adevărul: undeva aici.
//
// Razele sunt ORDINE DE MĂRIME, nu erori măsurate: o comună de câmpie are câțiva
// km, o stradă are sute de metri. Rotunde dinadins — o cifră precisă pentru o
// imprecizie ar fi tot o precizie inventată.
const RAZA_M: Record<string, number> = {
  localitate: 2000,
  strada: 250,
};

const CAT_DE_EXACT: Record<string, Cheie> = {
  contur: "struct.gropi.exact_contur",
  punct: "struct.gropi.exact_punct",
  strada: "struct.gropi.exact_strada",
  localitate: "struct.gropi.exact_localitate",
};

const groapaIcon = (aleasa: boolean) => L.divIcon({
  className: "",
  html: `<div class="gh-groapa${aleasa ? " gh-aleasa" : ""}">${
    aleasa ? "★" : ""}</div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

export default function GropiHarta({
  date, peAlegere, ocupat,
}: {
  date: GropiRaspuns;
  peAlegere: (code: string) => void;
  /** cât timp se salvează o alegere, harta nu mai acceptă clicuri */
  ocupat?: boolean;
}) {
  const gazda = useRef<HTMLDivElement | null>(null);
  const harta = useRef<L.Map | null>(null);
  const stratul = useRef<L.LayerGroup | null>(null);
  const [cfg, setCfg] = useState<ConfigHarta | null>(null);
  // Harta trăiește într-un `ref`, care nu declanșează randări; fără steagul
  // ăsta, efectul de desen ar rula o singură dată, înainte să existe harta.
  const [viu, setViu] = useState(false);
  const incadrat = useRef(false);

  useEffect(() => { configHarta().then(setCfg).catch(() => setCfg(null)); }, []);

  useEffect(() => {
    if (!cfg || !gazda.current || harta.current) return;
    // Vedere inițială EXPLICITĂ. Fără ea, harta e „fără vedere" până la prima
    // încadrare, iar orice `invalidateSize` de dinainte lucrează pe o stare pe
    // care Leaflet n-o are — de acolo ies zoom-uri care sar.
    const m = L.map(gazda.current, {
      center: [44.4268, 26.1025], zoom: 10,
      zoomControl: true, attributionControl: true,
    });
    L.tileLayer(cfg.tile_url, {
      maxZoom: cfg.max_zoom, attribution: cfg.attribution,
    }).addTo(m);
    m.attributionControl.setPrefix(false);
    stratul.current = L.layerGroup().addTo(m);
    harta.current = m;
    setViu(true);
  }, [cfg]);

  useEffect(() => () => { harta.current?.remove(); harta.current = null; }, []);

  const t = useT();

  // Ce se desenează. Depinde de alegere, ca steaua să se mute fără reconstrucția
  // hărții.
  const alesCod = date.chosen?.code;
  const puncte = useMemo(() => {
    const p: L.LatLngTuple[] = date.options.map((o) => [o.lat, o.lon]);
    // Și punctele traseului: un ocol pe centură iese din dreptunghiul
    // lot–groapă, iar încadrarea l-ar tăia exact pe bucata care explică de ce
    // drumul e mai lung decât pare.
    for (const [la, lo] of date.route ?? []) p.push([la, lo]);
    if (date.site.lat != null) {
      // Marginile discului, nu centrul: încadrat pe centru, o zonă de 2 km ar
      // ieși pe jumătate din ecran.
      const raza = RAZA_M[date.site.precision ?? ""] ?? 0;
      const dlat = raza / 111_320;
      const dlon = raza / (111_320 * Math.cos(date.site.lat * Math.PI / 180));
      p.push([date.site.lat - dlat, date.site.lon - dlon],
             [date.site.lat + dlat, date.site.lon + dlon]);
    }
    return p;
  }, [date]);

  // Încadrarea se face O SINGURĂ DATĂ, și numai când harta chiar are dimensiune.
  //
  // Pe ecranul de deviz panoul stă într-un `<td>` care se așază DUPĂ montare:
  // la prima randare celula poate fi de lățime zero. `fitBounds` calculează
  // zoomul din dimensiunea pe care Leaflet crede că o are, deci acolo dă zoom 0
  // — harta lumii — și, o dată marcat „încadrat", rămâne așa. Cu garda, o
  // dimensiune necredibilă NU consumă singura încadrare: o reface observatorul
  // de dimensiune, când celula s-a așezat.
  const incadreaza = useCallback((m: L.Map) => {
    if (incadrat.current || !puncte.length) return;
    const el = gazda.current;
    if (!el || el.clientWidth < 50 || el.clientHeight < 50) return;
    m.fitBounds(L.latLngBounds(puncte).pad(0.15));
    incadrat.current = true;
  }, [puncte]);

  useEffect(() => {
    const m = harta.current, strat = stratul.current;
    if (!viu || !m || !strat) return;
    strat.clearLayers();

    if (date.site.lat != null) {
      const raza = RAZA_M[date.site.precision ?? ""];
      const cat = CAT_DE_EXACT[date.site.precision ?? ""];
      const spune = `${date.site.address || t("struct.gropi.lotul")}` +
        `<br><span class="gh-mic">${cat ? t(cat) : ""}</span>`;
      if (raza) {
        // Zona, nu punctul. Discul e ce se vede; punctul din mijloc rămâne doar
        // ca ancoră a distanțelor — care se măsoară de acolo, cu tot ce implică.
        L.circle([date.site.lat, date.site.lon], {
          radius: raza, className: "gh-zona", interactive: true,
        }).bindTooltip(spune, { direction: "top" }).addTo(strat);
      } else {
        L.marker([date.site.lat, date.site.lon], { icon: LOT, interactive: true })
          .bindTooltip(spune, { direction: "top" }).addTo(strat);
      }
    }

    for (const o of date.options) {
      const aleasa = o.code === alesCod;
      const mk = L.marker([o.lat, o.lon], {
        icon: groapaIcon(aleasa), zIndexOffset: aleasa ? 1000 : 0,
      }).addTo(strat);
      mk.bindTooltip(
        t("struct.gropi.tooltip", {
          nume: `<strong>${o.name}</strong>`,
          km: o.distance_km,
          cost: bani(Number(o.total)),
        }) + (aleasa ? t("struct.gropi.folosita") : ""),
        { direction: "top" });
      // Linia lot→groapă, doar pentru cea aleasă: cu cinci linii deodată harta
      // devine o pânză de păianjen din care nu se mai citește nimic.
      //
      // DESENUL TREBUIE SĂ SPUNĂ ACELAȘI LUCRU CA CIFRA. Cât timp aici se
      // desena mereu un segment drept, harta contrazicea tabelul care scria
      // „drum rutier" — iar între un text și un desen, omul crede desenul.
      // Traseul rutat se trage plin, pe drumurile reale; segmentul drept rămâne
      // punctat, ca să se citească din prima că e o aproximare, nu un traseu.
      if (aleasa && date.site.lat != null) {
        const rutat = date.route && date.route.length > 1;
        L.polyline(
          rutat
            ? (date.route as [number, number][]).map(([la, lo]) => [la, lo] as L.LatLngTuple)
            : [[date.site.lat, date.site.lon], [o.lat, o.lon]],
          rutat
            ? { className: "gh-drum gh-drum-rutat", weight: 4 }
            : { className: "gh-drum", weight: 2, dashArray: "4 4" })
          .bindTooltip(rutat
            ? t("struct.gropi.drum_sosea", { km: o.distance_km })
            : t("struct.gropi.linie_dreapta", { km: o.distance_km }))
          .addTo(strat);
      }
      if (!ocupat) mk.on("click", () => peAlegere(o.code));
    }

    incadreaza(m);
  }, [viu, date, alesCod, ocupat, peAlegere, puncte, incadreaza]);

  // Redimensionarea: părintele se așază după prima randare (v. antetul).
  useEffect(() => {
    const m = harta.current, el = gazda.current;
    if (!viu || !m || !el) return;
    const ro = new ResizeObserver(() => {
      m.invalidateSize();
      // Numai dacă încadrarea n-a apucat să se facă la o dimensiune credibilă.
      // NICIODATĂ după: aici s-ar bate cu omul care tocmai a dat zoom.
      incadreaza(m);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [viu, puncte, incadreaza]);

  if (!cfg) return <div className="sg-muted">{t("struct.gropi.se_incarca")}</div>;
  return <div ref={gazda} className="gh-harta" />;
}

/** Tabelul de sub hartă: aceleași gropi, cu cifrele pe față. */
export function GropiTabel({
  optiuni, alesCod, peAlegere, ocupat,
}: {
  optiuni: GroapaOptiune[];
  alesCod: string | null;
  peAlegere: (code: string) => void;
  ocupat?: boolean;
}) {
  const t = useT();
  // Coloana taxei de acces apare numai când o groapă o are: pe un lot din
  // Ilfov sau fără zonă ar fi o coloană de zerouri.
  const cuAcces = optiuni.some((o) => Number(o.access_cost ?? 0) !== 0);
  return (
    <table className="sg-table gh-tabel">
      <thead>
        <tr>
          <th></th>
          <th>{t("struct.gropi.col_groapa")}</th>
          <th>{t("struct.gropi.col_distanta")}</th>
          <th>{t("struct.gropi.col_curse")}</th>
          <th>{t("struct.gropi.col_transport")}</th>
          <th>{t("struct.gropi.col_depozitare")}</th>
          {cuAcces && <th title={t("struct.gropi.acces_titlu")}>{t("struct.gropi.col_acces")}</th>}
          <th>{t("struct.gropi.col_total")}</th>
        </tr>
      </thead>
      <tbody>
        {optiuni.map((o) => (
          <tr key={o.code}
              className={o.code === alesCod ? "gh-rand-ales" : undefined}
              onClick={() => !ocupat && o.code !== alesCod && peAlegere(o.code)}
              style={{ cursor: ocupat || o.code === alesCod ? "default" : "pointer" }}>
            <td className="sg-mono">{o.code === alesCod ? "★" : ""}</td>
            <td>{o.name}
              {o.in_range === false && (
                <span className="gh-eticheta" title={t("struct.gropi.afara_raza_titlu")}>
                  {t("struct.gropi.afara_raza")}</span>
              )}
              <div className="sg-muted">{o.address}</div></td>
            {/* `≈` numai pe gropile care N-AU putut fi rutate. Semnul stă
                lângă cifră, nu într-o legendă de sub tabel: cine compară două
                rânduri trebuie să vadă din prima că unul e măsurat pe șosea și
                celălalt e o linie dreaptă înmulțită cu 1,3 — altfel diferența
                dintre ele pare o diferență de geografie, când e una de metodă. */}
            <td className="sg-mono" title={t(o.distance_source === "estimated"
                  ? "struct.gropi.estimare_titlu"
                  : "struct.gropi.masurat_titlu")}>
              {o.distance_source === "estimated" ? "≈ " : ""}{o.distance_km} km
            </td>
            <td className="sg-mono">{o.trips}</td>
            <td className="sg-mono">{bani(Number(o.transport_cost))}</td>
            <td className="sg-mono">{bani(Number(o.disposal_cost))}</td>
            {cuAcces && <td className="sg-mono">{bani(Number(o.access_cost ?? 0))}</td>}
            <td className="sg-mono"><strong>{bani(Number(o.total))}</strong></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
